import { defineStore } from 'pinia';
import { cloneDeep, get, each, find, findIndex, map } from 'lodash';
import Vue from 'vue';
import uniqid from 'uniqid';

import { adminClient as apolloClient } from '@/store/apollo';
import MemberTagApi from '@/extensions/restrictedArea/store/api/memberTag';
import MemberApi from '@/extensions/restrictedArea/store/api/member';
import SentEmailsGql from '@/store/graphql/system/queries/SentEmails.gql';
import CustomerApi from '@/extensions/shop/store/api/customer';
import CustomerGroupApi from '@/extensions/shop/store/api/customerGroup';
import SubscriptionTopicApi from '@/extensions/newsletter/store/api/subscriptionTopic';
import TaxApi from '@/extensions/shop/store/api/tax';
import FileApi from '@/store/api/cms/file';
import pinia from '@/pinia';
import { useSetupStore } from '@/stores';

const defaultForm = {
    status: 'enabled',
    gender: 'M',
    salutation: '',
    first_name: '',
    last_name: '',
    email: '',
    emailVerified: true,
    sendEmailVerification: false,
    preferred_correspondence_email: '',
    password: '',
    changePassword: false,
    phone: '',
    mobile: '',
    whatsapp_order_updates_consent: false,
    date_of_birth: null,
    company: '',
    memberTags: [],
    subscriptionTopics: [],
    addresses: [
        {
            type: 'main',
            street: '',
            zipcode: '',
            city: '',
            country: '',
            company: '',
            company_addition: '',
            line_1: '',
            line_2: '',
            line_3: '',
            latitude: null,
            longitude: null,
            default_billing: true,
            default_shipping: true,
        },
    ],
    customer: {
        nr: undefined,
        customerGroup: undefined,
        show_net_prices: false,
        tax_free: false,
    },
    cms: {
        id: undefined,
    },
};

const defaultRules = {
    gender: 'required',
    first_name: 'required',
    last_name: 'required',
    email: 'required|email',
    preferred_correspondence_email: 'email',
    password: 'required',
    phone: '',
    mobile: '',
    company: '',
    date_of_birth: '',
    customer: {
        nr: '',
        customerGroup: '',
    },
};

const defaultUi = {
    form: {
        initialized: false,
        validateOnEnter: false,
    },
};

export const useRestrictedAreaMemberStore = defineStore('restrictedAreaMember', {
    state: () => ({
        loading: true,
        membersPaginated: {
            path: null,
            variables: {
                paginationSizes: [25, 50, 100, 500],
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0,
                selection: {
                    type: 'inclusive',
                    identifiers: [],
                    total: 0,
                },
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: [],
        },
        loadingFiles: true,
        memberFilesPaginated: {
            path: null,
            variables: {
                paginationSizes: [25, 50, 100],
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0,
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            link: {},
            items: [],
        },
        options: {
            customerGroup: {
                loading: false,
                items: [],
            },
            tax: {
                loading: false,
                items: [],
            },
            memberTag: {
                loading: false,
                items: []
            },
            subscriptionTopic: {
                loading: false,
                items: []
            },
        },
        verificationEmails: [],
        verificationEmailsLoading: false,
        form: cloneDeep(defaultForm),
        rules: cloneDeep(defaultRules),
        ui: cloneDeep(defaultUi),
    }),

    getters: {
        formInitialized: (state) => state.ui.form.initialized,
        formValidateOnEnter: (state) => state.ui.form.validateOnEnter,
        formAddresses: (state) => state.form.addresses,
    },

    actions: {
        // List mutations
        addPaginatedMembersToState(data) {
            this.membersPaginated.items = data.items;
            this.membersPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.membersPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.membersPaginated.link = cloneDeep(data.config.link);
            this.membersPaginated.showFilters = get(data.config, 'showFilters') || false;
            this.membersPaginated.defaultSort = get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.membersPaginated.allColumns, (column) => {
                let allowed = find(activeColumnKeys, (value) => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.membersPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.membersPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.membersPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.membersPaginated.filters, key);
            } else {
                Vue.set(this.membersPaginated.filters, key, value);
            }
        },

        defineFilterVisibility(value) {
            Vue.set(this.membersPaginated, 'showFilters', value);
        },

        defineSorting({ key, value }) {
            Vue.set(this.membersPaginated.variables, 'order', key);
            Vue.set(this.membersPaginated.variables, 'direction', value);
        },

        paginationBulkSelectionCheckAll({ value }) {
            let selection = this.membersPaginated.pageInfo.selection;
            if (value === true) {
                Vue.set(selection, 'type', 'exclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', this.membersPaginated.pageInfo.total);
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', 0);
            }
        },

        paginationBulkSelectionCheckVisible({ value }) {
            const items = get(this.membersPaginated, 'items') || [];
            const ids = items.map(el => parseInt(el.id));

            let selection = this.membersPaginated.pageInfo.selection;
            if (value === true) {
                Vue.set(selection, 'type', 'inclusive');

                if (!selection.identifiers.length) {
                    Vue.set(selection, 'identifiers', ids);
                } else {
                    Vue.set(selection, 'identifiers', [...selection.identifiers, ...ids]);
                }
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(selection, 'identifiers', selection.identifiers.filter(el => ids.includes(el) === false));
            }

            Vue.set(selection, 'identifiers', [...new Set(selection.identifiers)]);
            Vue.set(selection, 'total', selection.identifiers.length);
        },

        paginationBulkSelectionCheckItem(item) {
            let selection = this.membersPaginated.pageInfo.selection;
            const id = parseInt(item.id);

            let checked = false;
            switch (selection.type) {
                case 'inclusive':
                    checked = !!~selection.identifiers.map(el => parseInt(el)).indexOf(id);
                    break;
                case 'exclusive':
                    checked = !~selection.identifiers.map(el => parseInt(el)).indexOf(id);
                    break;
            }

            let push = true;
            let incr = true;
            switch (selection.type) {
                case 'inclusive':
                    push = incr = checked === false;
                    break;
                case 'exclusive':
                    push = checked === true;
                    incr = checked === false;
                    break;
            }

            if (push === true) {
                const index = selection.identifiers.length;
                Vue.set(selection.identifiers, index, id);
            } else {
                const index = selection.identifiers.indexOf(id);
                if (index >= 0) Vue.delete(selection.identifiers, index);
            }

            if (incr === true) Vue.set(selection, 'total', selection.total + 1);
            else Vue.set(selection, 'total', selection.total - 1);
        },

        paginationBulkSelectionReset() {
            Vue.set(this.membersPaginated.pageInfo, 'selection', {
                type: 'inclusive',
                identifiers: [],
                total: 0,
            });
        },

        processPaginationBulkAction(action, options) {
            return MemberApi.processMemberPaginationBulkAction(action, this.membersPaginated, options);
        },

        updateLoading(value) {
            this.loading = value;
        },

        // Member files pagination
        addPaginatedMemberFilesToState(data) {
            this.memberFilesPaginated.items = data.items;
            this.memberFilesPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.memberFilesPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.memberFilesPaginated.link = cloneDeep(data.config.link);
            this.memberFilesPaginated.showFilters = get(data.config, 'showFilters') || false;
        },

        defineActiveMemberFilesColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.memberFilesPaginated.allColumns, (column) => {
                let allowed = find(activeColumnKeys, (value) => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.memberFilesPaginated.activeColumns = activeColumns;
            }
        },

        defineMemberFileItemsPerPage(perPage) {
            this.memberFilesPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentMemberFilesPage(currentPage) {
            this.memberFilesPaginated.pageInfo.currentPage = currentPage;
        },

        defineMemberFilesFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.memberFilesPaginated.filters, key);
            } else {
                Vue.set(this.memberFilesPaginated.filters, key, value);
            }
        },

        defineMemberFilesFilterVisibility(value) {
            Vue.set(this.memberFilesPaginated, 'showFilters', value);
        },

        updateLoadingFiles(value) {
            this.loadingFiles = value;
        },

        updateRule({ key, rules }) {
            Vue.set(this.rules, key, rules);
        },

        resetRules() {
            Vue.set(this, 'rules', cloneDeep(defaultRules));
            if (this.form.id) Vue.set(this.rules, 'password', '');

            const setupStore = useSetupStore(pinia);
            if (setupStore.enabledExtensions && setupStore.enabledExtensions.includes('shop')) {
                Vue.set(this.rules, 'customer', {
                    nr: 'required',
                    customerGroup: 'required',
                });
            }
        },

        resetUI() {
            Vue.set(this, 'ui', cloneDeep(defaultUi));
        },

        insertMember(member) {
            const setupStore = useSetupStore(pinia);
            if (setupStore.enabledExtensions && setupStore.enabledExtensions.includes('shop') && !member.customer) {
                const defaultCustomerGroup = this.options.customerGroup.items.find((item) => item.default);
                member.customer = {
                    id: uniqid('new:'),
                    nr: undefined,
                    customerGroup: defaultCustomerGroup,
                };
            }

            member.emailVerified = !!get(member, 'email_verified_at');
            member.sendEmailVerification = false;

            Vue.set(this, 'form', member);
        },

        // Options loading
        updateCustomerGroupOptionsLoading(value) {
            Vue.set(this.options.customerGroup, 'loading', value);
        },

        insertCustomerGroupOptions(customerGroups) {
            Vue.set(this.options.customerGroup, 'items', customerGroups);
        },

        updateTaxOptionsLoading(value) {
            Vue.set(this.options.tax, 'loading', value);
        },

        insertTaxOptions(taxes) {
            Vue.set(this.options.tax, 'items', taxes);
        },

        updateMemberTagOptionsLoading(value) {
            Vue.set(this.options.memberTag, 'loading', value);
        },

        insertMemberTagOptions(tags) {
            Vue.set(this.options.memberTag, 'items', tags);
        },

        updateSubscriptionTopicOptionsLoading(value) {
            Vue.set(this.options.subscriptionTopic, 'loading', value);
        },

        insertSubscriptionTopicOptions(topics) {
            Vue.set(this.options.subscriptionTopic, 'items', topics);
        },

        // Form mutations
        setFormInitialized(flag) {
            Vue.set(this.ui.form, 'initialized', flag);
        },

        setFormValidateOnEnter(flag) {
            Vue.set(this.ui.form, 'validateOnEnter', flag);
        },

        setFormStatus(value) {
            Vue.set(this.form, 'status', value);
        },

        setFormGender(value) {
            Vue.set(this.form, 'gender', value);
        },

        setFormSalutation(value) {
            Vue.set(this.form, 'salutation', value);
        },

        setFormFirstName(value) {
            Vue.set(this.form, 'first_name', value);
        },

        setFormLastName(value) {
            Vue.set(this.form, 'last_name', value);
        },

        setFormEmail(value) {
            Vue.set(this.form, 'email', value);
        },

        setFormPreferredCorrespondenceEmail(value) {
            Vue.set(this.form, 'preferred_correspondence_email', value);
        },

        setFormPassword(value) {
            Vue.set(this.form, 'password', value);
        },

        setFormChangePassword(value) {
            Vue.set(this.form, 'changePassword', value);
            if (value) Vue.set(this.rules, 'password', 'required');
            else Vue.set(this.rules, 'password', '');
        },

        setFormPhone(value) {
            Vue.set(this.form, 'phone', value);
        },

        setFormWhatsappOrderUpdatesConsent(value) {
            Vue.set(this.form, 'whatsapp_order_updates_consent', value);
        },

        setFormCompany(value) {
            Vue.set(this.form, 'company', value);
        },

        setFormEmailVerified(value) {
            Vue.set(this.form, 'emailVerified', value);
            if (value) {
                Vue.set(this.form, 'sendEmailVerification', false);
            }
        },

        setFormSendEmailVerification(value) {
            Vue.set(this.form, 'sendEmailVerification', value);
        },

        resendEmailVerification() {
            var member = cloneDeep(this.form);
            member.sendEmailVerification = true;
            return MemberApi.update(member).then(member => {
                this.insertMember(cloneDeep(member));
                return member;
            });
        },

        setFormSubscriptionTopics(topics) {
            Vue.set(this.form, 'subscriptionTopics', topics);
        },

        // Address mutations
        addFormAddress() {
            const i = get(this.form, 'addresses.length') || 0;

            let type = 'main';
            let default_billing = true;
            let default_shipping = true;

            if (i > 0) {
                type = 'additional';
                default_billing = false;
                default_shipping = false;
            }

            Vue.set(this.form.addresses, i, {
                type,
                street: '',
                zipcode: '',
                city: '',
                country: '',
                line_1: '',
                line_2: '',
                line_3: '',
                default_billing,
                default_shipping,
            });
        },

        markFormAddressAsDeleted(i) {
            Vue.set(this.form.addresses[i], 'deleted', true);
            Vue.set(this.rules.addresses, i, {});
        },

        unmarkFormAddressAsDeleted(i) {
            Vue.set(this.form.addresses[i], 'deleted', false);
        },

        setFormAddressGender({ i, value }) {
            Vue.set(this.form.addresses[i], 'gender', value);
        },

        setFormAddressFirstName({ i, value }) {
            Vue.set(this.form.addresses[i], 'first_name', value);
        },

        setFormAddressLastName({ i, value }) {
            Vue.set(this.form.addresses[i], 'last_name', value);
        },

        setFormAddressEmail({ i, value }) {
            Vue.set(this.form.addresses[i], 'email', value);
        },

        setFormAddressPhone({ i, value }) {
            Vue.set(this.form.addresses[i], 'phone', value);
        },

        setFormAddressStreet({ i, value }) {
            Vue.set(this.form.addresses[i], 'street', value);
        },

        setFormAddressZipcode({ i, value }) {
            Vue.set(this.form.addresses[i], 'zipcode', value);
        },

        setFormAddressCity({ i, value }) {
            Vue.set(this.form.addresses[i], 'city', value);
        },

        setFormAddressCountry({ i, value }) {
            Vue.set(this.form.addresses[i], 'country', value);
        },

        setFormAddressCompany({ i, value }) {
            Vue.set(this.form.addresses[i], 'company', value);
        },

        setFormAddressCompanyAddition({ i, value }) {
            Vue.set(this.form.addresses[i], 'company_addition', value);
        },

        setFormAddressTaxId({ i, value }) {
            Vue.set(this.form.addresses[i], 'tax_id', value);
        },

        setFormAddressTaxNr({ i, value }) {
            Vue.set(this.form.addresses[i], 'tax_nr', value);
        },

        setFormAddressUrl({ i, value }) {
            Vue.set(this.form.addresses[i], 'url', value);
        },

        setFormAddressLine1({ i, value }) {
            Vue.set(this.form.addresses[i], 'line_1', value);
        },

        setFormAddressLine2({ i, value }) {
            Vue.set(this.form.addresses[i], 'line_2', value);
        },

        setFormAddressLine3({ i, value }) {
            Vue.set(this.form.addresses[i], 'line_3', value);
        },

        setFormAddressPublic({ i, value }) {
            Vue.set(this.form.addresses[i], 'public', value);
        },

        setFormDefaultBilling({ i, value }) {
            if (value) {
                this.form.addresses.forEach((address, index) => {
                    if (i !== index) Vue.set(this.form.addresses[index], 'default_billing', false);
                });
                Vue.set(this.form.addresses[i], 'default_billing', value);
            }
        },

        setFormDefaultShipping({ i, value }) {
            if (value) {
                this.form.addresses.forEach((address, index) => {
                    if (i !== index) Vue.set(this.form.addresses[index], 'default_shipping', false);
                });
                Vue.set(this.form.addresses[i], 'default_shipping', value);
            }
        },

        setFormLatitude({ i, value }) {
            Vue.set(this.form.addresses[i], 'latitude', value);
        },

        setFormLongitude({ i, value }) {
            Vue.set(this.form.addresses[i], 'longitude', value);
        },

        // Media mutations
        addFormMedia(media) {
            const index = get(this, 'form.media.length', 0);
            Vue.set(this.form.media, index, media);
        },

        sortFormMedia(media) {
            const currentMedia = get(this.form, 'media') || [];

            media.forEach((item, index) => {
                const m = findIndex(currentMedia, { id: item.id });

                if (m >= 0) {
                    Vue.set(this.form.media[m], 'position', index + 1);
                }
            });
        },

        markFormMediaAsDeleted(i) {
            Vue.set(this.form.media[i], 'deleted', true);
        },

        unmarkFormMediaAsDeleted(i) {
            Vue.set(this.form.media[i], 'deleted', false);
        },

        updateFormMediaVisibility({ media, scope, value }) {
            const currentMedia = get(this.form, 'media') || [];
            const m = findIndex(currentMedia, { id: media.id });
            if (m >= 0) {
                if (this.form.media[m].settings === undefined) Vue.set(this.form.media[m], 'settings', {});
                if (this.form.media[m].settings.show === undefined) Vue.set(this.form.media[m].settings, 'show', {});
                Vue.set(this.form.media[m].settings.show, scope, value);
            }
        },

        // Customer mutations
        setFormCustomerNr(value) {
            Vue.set(this.form.customer, 'nr', value);
        },

        setFormCustomerGroup(value) {
            Vue.set(this.form.customer, 'customerGroup', value);
        },

        setFormCustomerShowNetPrices(value) {
            Vue.set(this.form.customer, 'show_net_prices', value);
        },

        setFormCustomerTaxFree(value) {
            Vue.set(this.form.customer, 'tax_free', value);
        },

        setFormCustomerShowPointOfSalePurchases(value) {
            Vue.set(this.form.customer, 'show_point_of_sale_purchases', value);
        },

        deleteFormCustomerCoupon(coupon) {
            const index = findIndex(this.form.customer.coupons, { id: coupon.id });
            if (index >= 0) Vue.delete(this.form.customer.coupons, index);
        },

        markFormCustomerCouponAsDeleted(coupon) {
            const index = findIndex(this.form.customer.coupons, { id: coupon.id });
            if (index >= 0) Vue.set(this.form.customer.coupons[index], 'deleted', true);
        },

        unmarkFormCustomerCouponAsDeleted(coupon) {
            const index = findIndex(this.form.customer.coupons, { id: coupon.id });
            if (index >= 0) Vue.set(this.form.customer.coupons[index], 'deleted', false);
        },

        setFormCustomerCoupon(coupon) {
            let index = findIndex(this.form.customer.coupons, { id: coupon.id });
            if (index < 0) index = get(this.form, 'customer.coupons.length') || 0;
            Vue.set(this.form.customer.coupons, index, coupon);
        },

        // Member tags
        setFormMemberTags({ s, tags }) {
            Vue.set(this.form.memberTags[s], 'tags', tags);
        },

        addFormMemberTag(name) {
            let tag;

            // find existing by name
            if (this.options.memberTag.items.length) {
                const match = this.options.memberTag.items.find(tag => tag.name === name);
                if (match) tag = match;
            }

            // check if tag is already in use
            if (this.form.memberTags.length) {
                const match = this.form.memberTags.find(tag => tag.name === name);
                if (match) return;
            }

            if (name) {
                if (!tag) tag = { name };
                const i = this.form.memberTags.length;
                Vue.set(this.form.memberTags, i, tag);
            }
        },

        removeFormMemberTag(tag) {
            const index = findIndex(this.form.memberTags, { name: tag.name });
            if (index >= 0) Vue.delete(this.form.memberTags, index);
        },

        // Member files
        markMemberFileAsDeleted(file) {
            if (!this.form.files) Vue.set(this.form, 'files', []);

            let index = findIndex(this.form.files, { id: file.id });
            if (index < 0) index = get(this.form, 'files.length') || 0;

            file = cloneDeep(file);
            file.deleted = true;

            Vue.set(this.form.files, index, file);
        },

        unmarkMemberFileAsDeleted(file) {
            if (this.form.files) {
                let index = findIndex(this.form.files, { id: file.id });
                if (index >= 0) Vue.delete(this.form.files, index);
            }
        },

        // Async actions
        fetchMembersPaginated() {
            return MemberApi.fetchMembersPaginated(this.membersPaginated).then((data) => {
                this.addPaginatedMembersToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchMemberFilesPaginated(payload = {}) {
            return FileApi.fetchFilesPaginated({ ...payload, ...this.memberFilesPaginated }).then((data) => {
                this.addPaginatedMemberFilesToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineActiveMemberFilesColumns(activeColumnKeys);
            });
        },

        fetchMember(id) {
            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;

            return MemberApi.fetchMember({ id, cms: cms.id }).then((member) => {
                this.insertMember(cloneDeep(member));
            });
        },

        fetchCustomerGroupOptions(cmsID) {
            this.updateCustomerGroupOptionsLoading(true);
            return CustomerGroupApi.fetchList(cmsID)
                .then((customerGroups) => {
                    this.updateCustomerGroupOptionsLoading(false);
                    this.insertCustomerGroupOptions(customerGroups);
                })
                .catch((err) => {
                    this.updateCustomerGroupOptionsLoading(false);
                    throw err;
                });
        },

        fetchTaxOptions(cmsID) {
            this.updateTaxOptionsLoading(true);
            return TaxApi.fetchList(cmsID)
                .then((taxes) => {
                    this.updateTaxOptionsLoading(false);
                    this.insertTaxOptions(taxes);
                })
                .catch((err) => {
                    this.updateTaxOptionsLoading(false);
                    throw err;
                });
        },

        addFileToMember(file) {
            let member = {
                id: this.form.id,
                files: [file],
            };
            return MemberApi.update(member);
        },

        create(member) {
            member = cloneDeep(member);

            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;
            member.cms = { id: cms.id };

            return MemberApi.create(member).then((member) => {
                this.insertMember(cloneDeep(member));
                this.resetRules();
                return member;
            });
        },

        update(member) {
            return MemberApi.update(member).then((member) => {
                this.insertMember(cloneDeep(member));
                this.resetRules();
                return member;
            });
        },

        delete(id) {
            return MemberApi.delete(id).then(() => {
                this.reset();
                this.resetRules();
                this.resetUI();
            });
        },

        fetchVerificationEmails() {
            const email = get(this.form, 'email');
            if (!email) return Promise.resolve([]);

            this.verificationEmailsLoading = true;
            return apolloClient
                .query({
                    query: SentEmailsGql,
                    variables: {
                        recipient_email: email,
                        type: 'verify-email',
                    },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => {
                    this.verificationEmails = data.SentEmails || [];
                    return this.verificationEmails;
                })
                .finally(() => {
                    this.verificationEmailsLoading = false;
                });
        },

        reset() {
            let member = cloneDeep(defaultForm);
            const setupStore = useSetupStore(pinia);
            if (setupStore.enabledExtensions && setupStore.enabledExtensions.includes('shop')) {
                const defaultCustomerGroup = this.options.customerGroup.items.find((item) => item.default);
                member.customer = {
                    id: uniqid('new:'),
                    nr: undefined,
                    customerGroup: defaultCustomerGroup,
                };
                this.insertMember(member);

                const rules = {
                    nr: 'required',
                    customerGroup: 'required',
                };
                this.updateRule({ key: 'customer', rules });
            } else {
                this.insertMember(member);
            }
        },

        fetchTagOptions(cmsID) {
            this.updateMemberTagOptionsLoading(true);
            return MemberTagApi.fetchList(cmsID)
                .then(tags => {
                    this.updateMemberTagOptionsLoading(false);
                    this.insertMemberTagOptions(tags);
                })
                .catch(err => {
                    this.updateMemberTagOptionsLoading(false);
                    throw err;
                });
        },

        fetchSubscriptionTopicOptions(cmsID) {
            this.updateSubscriptionTopicOptionsLoading(true);
            return SubscriptionTopicApi.fetchList(cmsID)
                .then(subscriptionTopics => {
                    this.updateSubscriptionTopicOptionsLoading(false);
                    this.insertSubscriptionTopicOptions(subscriptionTopics);
                })
                .catch(err => {
                    this.updateSubscriptionTopicOptionsLoading(false);
                    throw err;
                });
        },

        initializeMemberImport({ shopID, cmsID, file }) {
            return MemberApi.initializeMemberImport(shopID, cmsID, file);
        },

        validateMemberImportCsv({ shopID, cmsID, file }) {
            return MemberApi.validateMemberImportCsv(shopID, cmsID, file);
        },

        validateEmailDeliverability(email) {
            const setupStore = useSetupStore(pinia);
            const cmsId = setupStore.activeCms.id;
            return MemberApi.validateEmailDeliverability(email, cmsId);
        },

        fetchMemberApiTokens(memberId) {
            return MemberApi.fetchApiTokens(memberId);
        },

        createMemberApiToken(memberId, name) {
            return MemberApi.createApiToken(memberId, name);
        },

        deleteMemberApiToken(memberId, tokenId) {
            return MemberApi.deleteApiToken(memberId, tokenId);
        },
    }
});
